import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mic, Sparkles, Gamepad2, Share2, ShieldCheck, Rocket, GraduationCap, Baby, ArrowRight, Wand2, Trophy, ChevronDown, Joystick, ShoppingBag, Coins, Swords, Check, QrCode } from "lucide-react";
import Logo from "@/components/Logo";
import CommunityClips from "@/components/CommunityClips";
import ClipOfTheWeek from "@/components/ClipOfTheWeek";
import CinematicWaitlist from "@/components/CinematicWaitlist";
import FoundingWaitlist from "@/components/FoundingWaitlist";
import LaunchTrustBadges from "@/components/LaunchTrustBadges";
import { Button } from "@/components/ui/button";
import { useMode } from "@/context/ModeContext";
import { useAuth } from "@/context/AuthContext";

const STEPS = [
  { icon: Mic, title: "Speak your idea", text: "“A cat skateboards through space collecting fish.” Just say it out loud." },
  { icon: Wand2, title: "Studio plans it", text: "Our AI designs a full game plan — character, world, goal and difficulty." },
  { icon: Gamepad2, title: "Play instantly", text: "A real, playable game appears in your browser in seconds." },
  { icon: Share2, title: "Publish safely", text: "Share to the Arcade — kid games get parent approval first." },
];

const ROLES = [
  { icon: Baby, title: "Kids", slug: "kids", text: "Imagine it. Say it. Play it. Voice-first, big buttons, zero code — safe by design.", c: "from-amber-400 to-orange-500" },
  { icon: Joystick, title: "Teens", slug: "teens", text: "Build the game everyone at school wants to play — multiplayer, clans and tournaments.", c: "from-[#3b9eff] to-[#1f6feb]" },
  { icon: Rocket, title: "Creators", slug: "creators", text: "Your first game starts with one prompt. Templates, live preview, one-click publishing.", c: "from-[#a855f7] to-[#7c3aed]" },
  { icon: Swords, title: "Studios", slug: "studios", text: "Prototype faster. Build smarter. Launch more — AI world-building and play testing.", c: "from-[#22c55e] to-[#14b8a6]" },
  { icon: GraduationCap, title: "Schools", slug: "schools", text: "Turn students from players into creators — classrooms, projects and safe showcases.", c: "from-violet-400 to-indigo-500" },
  { icon: ShieldCheck, title: "Parents", slug: "parents", text: "More than screen time. Creative time — approve games, set limits, control privacy.", c: "from-emerald-400 to-teal-500" },
];

const TEMPLATES = ["🏃 Platformer", "🏆 Runner", "🚀 Shooter", "🧩 Maze", "🔨 Whack", "❓ Quiz", "🃏 Card Match", "🏎️ Racing"];

const TRUST_PILLARS = [
  { icon: ShieldCheck, title: "Certified original", text: "Every game is scanned for protected IP and issued a Certificate of Authenticity — proof it's clean, original work.", c: "from-emerald-400 to-teal-500" },
  { icon: QrCode, title: "Instantly verifiable", text: "A scannable certificate + public verify page lets anyone confirm authorship and provenance in one tap.", c: "from-[#3b9eff] to-[#1f6feb]" },
  { icon: Coins, title: "Yours to own & sell", text: "Export to Unity, Unreal or Blender with the licence attached — publish, monetise, and keep the credit.", c: "from-amber-400 to-orange-500" },
];

export default function Landing() {
  const navigate = useNavigate();
  const { setMode } = useMode();
  const { user } = useAuth();
  const [verified, setVerified] = useState([]);
  useEffect(() => { setMode("studio"); }, [setMode]);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/arcade/verified?limit=8`)
      .then((r) => r.json()).then((d) => setVerified(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);
  const startCreating = () => navigate(user ? "/app/create" : "/register");

  return (
    <div className="min-h-screen bg-[#05060a] text-[hsl(var(--foreground))] overflow-x-hidden">
      {/* Nav */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-black/40 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <Logo to="/" size="md" animated />
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate("/login")} data-testid="nav-login-btn">Log in</Button>
            <Button onClick={() => navigate("/register")} data-testid="nav-getstarted-btn"
              className="rounded-full bg-gradient-to-r from-[#1f6feb] to-[#3b9eff] hover:opacity-90 brand-glow">
              Get Started <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Hero — cinematic image background */}
      <section className="relative min-h-screen flex flex-col items-center justify-center px-5 text-center overflow-hidden" data-testid="hero-splash">
        {/* Welcome-page background image (fades in first) */}
        <div className="absolute inset-0 z-0 animate-rise" style={{ animationDuration: ".9s" }}>
          <img
            src="/brand/welcome-bg.webp"
            alt="GameView fantasy world"
            data-testid="hero-bg-image"
            className="w-full h-full object-cover"
            style={{ objectPosition: "center 42%" }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-[#05060a]" />
          <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 40%, transparent 55%, rgba(5,6,10,0.45) 100%)" }} />
        </div>

        {/* Soft floating particles */}
        <Particles />

        <div className="relative z-10 flex flex-col items-center -translate-y-6">
          {/* Original GameView Studio logo — identical to the top-left brand; spins in then idle-floats */}
          <div className="animate-logo-float" style={{ animationDelay: "2s" }}>
            <div className="relative grid place-items-center">
              <div className="absolute w-[340px] h-[190px] max-w-[82vw] rounded-full bg-[#3b9eff]/18 blur-3xl animate-halo" />
              <div className="absolute w-[230px] h-[135px] max-w-[60vw] rounded-full bg-[#a855f7]/14 blur-3xl animate-halo" style={{ animationDelay: "1.5s" }} />
              <img
                src="/brand/gv-logo-full.png"
                alt="GameView Studio"
                data-testid="hero-logo"
                className="relative hero-logo-anim h-12 sm:h-16 lg:h-20 w-auto max-w-[82vw] object-contain"
              />
            </div>
          </div>

          {/* Slogan */}
          <p
            className="fade-up mt-4 font-display text-base sm:text-lg font-semibold tracking-tight text-white/90 drop-shadow-[0_2px_14px_rgba(0,0,0,0.9)]"
            style={{ animationDelay: "1.7s" }}
            data-testid="hero-slogan"
          >
            Create Any Game You Imagine.
          </p>

          {/* Trust differentiator pill */}
          <div className="fade-up mt-4 inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur px-4 py-1.5 border border-white/15" style={{ animationDelay: "1.9s" }} data-testid="hero-trust-pill">
            <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
            <span className="text-xs sm:text-sm text-white/90 font-medium">Every game certified <b className="text-white">original</b> — verifiable, ownable, monetisable</span>
          </div>

          {/* Enter button — appears last, soft hover glow */}
          <div className="fade-up mt-8" style={{ animationDelay: "2.05s" }}>
            <Button
              size="default"
              onClick={startCreating}
              data-testid="hero-enter-btn"
              className="group rounded-full text-sm sm:text-base px-7 py-5 font-bold text-white bg-gradient-to-r from-[#7c3aed] via-[#1f6feb] to-[#3b9eff] btn-glow"
            >
              <Sparkles className="w-4 h-4" /> Let's Create
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>
        </div>

        {/* Scroll hint */}
        <div className="fade-up absolute bottom-8 inset-x-0 flex justify-center text-white/40 z-10" style={{ animationDelay: "2.4s" }}>
          <div className="flex flex-col items-center gap-1 animate-bob">
            <span className="text-xs uppercase tracking-[0.2em]">Scroll to explore</span>
            <ChevronDown className="w-5 h-5" />
          </div>
        </div>
      </section>

      {/* Launch trust badges (§15/§17) */}
      <section className="relative z-10 px-5 pt-4 pb-8">
        <LaunchTrustBadges />
      </section>

      {/* Magic loop */}
      <section className="relative z-10 max-w-6xl mx-auto px-5 py-16">
        <h2 className="font-display text-3xl sm:text-4xl font-bold text-center mb-3">The magic loop</h2>
        <p className="text-center text-white/60 mb-12">Idea → Plan → Play → Publish. In minutes.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {STEPS.map((s, i) => (
            <div key={s.title} className={`glass rounded-2xl p-6 card-hover animate-rise delay-${i + 1}`} data-testid={`step-${i}`}>
              <div className="w-12 h-12 rounded-xl grid place-items-center bg-gradient-to-br from-[#1f6feb] to-[#3b9eff] mb-4">
                <s.icon className="w-6 h-6 text-white" />
              </div>
              <div className="text-xs font-bold text-[#3b9eff] mb-1">STEP {i + 1}</div>
              <h3 className="font-display text-lg font-bold mb-2">{s.title}</h3>
              <p className="text-sm text-white/60">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Meet Studio — real prompts turning into games (marketing plan §5) */}
      <section className="relative z-10 max-w-6xl mx-auto px-5 py-16" data-testid="meet-studio">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-4 bg-white/5 border border-white/10 text-xs font-bold text-[#3b9eff]"><Wand2 className="w-4 h-4" /> MEET STUDIO — YOUR AI GAME PARTNER</div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold mb-3">Just say it. Studio builds it.</h2>
          <p className="text-white/60 max-w-2xl mx-auto">Ask Studio to create a world, design a character, balance difficulty or add a boss. Here are real prompts and what they become.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { prompt: "Create a space adventure where I rescue planets.", emoji: "🪐", title: "Planet Rescuer", genre: "Space adventure", c: "from-[#3b9eff] to-[#1f6feb]" },
            { prompt: "Turn this drawing into the main character.", emoji: "🎨", title: "My Hero, Alive", genre: "Custom character", c: "from-[#a855f7] to-[#7c3aed]" },
            { prompt: "Build ten levels that become progressively harder.", emoji: "🪜", title: "Rising Challenge", genre: "10-level platformer", c: "from-[#22c55e] to-[#14b8a6]" },
            { prompt: "Add a multiplayer racing mode.", emoji: "🏁", title: "Co-op Grand Prix", genre: "Multiplayer racer", c: "from-[#f59e0b] to-[#ef4444]" },
            { prompt: "Create a final boss battle inside a frozen castle.", emoji: "🐉", title: "Frostspire Boss", genre: "Boss battle", c: "from-[#06b6d4] to-[#3b9eff]" },
            { prompt: "Make a cosy shop game in a mushroom village.", emoji: "🍄", title: "Mushroom Market", genre: "Cosy sim", c: "from-[#ec4899] to-[#f43f5e]" },
          ].map((m, i) => (
            <button key={m.title} onClick={() => navigate(user ? `/app/create?idea=${encodeURIComponent(m.prompt)}` : "/register")} data-testid={`studio-prompt-${i}`}
              className="group glass rounded-2xl p-5 text-left card-hover">
              <div className="flex items-start gap-2 text-white/85 mb-4">
                <Mic className="w-4 h-4 mt-1 text-[#3b9eff] shrink-0" />
                <span className="font-mono text-sm leading-snug">"{m.prompt}"</span>
              </div>
              <div className="flex items-center gap-2 text-white/40 text-xs mb-3"><span className="h-px flex-1 bg-white/10" /> Studio builds <ArrowRight className="w-3 h-3" /></div>
              <div className="flex items-center gap-3 rounded-xl p-3 bg-white/[0.04] border border-white/10">
                <span className={`w-11 h-11 rounded-xl grid place-items-center text-2xl bg-gradient-to-br ${m.c}`}>{m.emoji}</span>
                <div className="min-w-0">
                  <div className="font-display font-bold truncate">{m.title}</div>
                  <div className="text-xs text-white/50">{m.genre}</div>
                </div>
                <Gamepad2 className="w-4 h-4 text-white/30 ml-auto group-hover:text-[#3b9eff] transition-colors" />
              </div>
            </button>
          ))}
        </div>
        <div className="text-center mt-8">
          <Button onClick={() => navigate(user ? "/app/create" : "/register")} size="lg" className="rounded-full bg-gradient-to-r from-[#1f6feb] to-[#3b9eff] btn-glow" data-testid="meet-studio-cta">
            <Sparkles className="w-5 h-5" /> Try your own prompt
          </Button>
        </div>
      </section>

      {/* Clip of the Week — auto-spotlight of the top player clip (renders only when a clip exists) */}
      <ClipOfTheWeek />

      {/* Staff Picks — community gameplay clips (renders only when curated) */}
      <CommunityClips featured variant="landing" title="Watch what players are making" sort="trending" limit={8} linkBase="/g" testid="landing-staff-picks" />

      {/* AAA Cinematic Mode public waitlist (renders only while teasing pre-launch) */}
      <CinematicWaitlist />

      {/* Own what you create — trust differentiator */}
      <section className="relative z-10 max-w-6xl mx-auto px-5 py-14" data-testid="trust-section">
        <div className="text-center mb-10">
          <h2 className="font-display text-3xl sm:text-4xl font-bold mb-3">The only AI game engine that proves it's <span className="bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">truly yours</span></h2>
          <p className="text-white/60 max-w-2xl mx-auto">Prompt it, build it, and walk away owning it — backed by a certificate anyone can verify.</p>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          {TRUST_PILLARS.map((p) => (
            <div key={p.title} className="glass rounded-2xl p-6 card-hover" data-testid={`trust-pillar-${p.title.split(" ")[0].toLowerCase()}`}>
              <div className={`w-12 h-12 rounded-xl grid place-items-center bg-gradient-to-br ${p.c} mb-4`}><p.icon className="w-6 h-6 text-white" /></div>
              <h3 className="font-display text-lg font-bold mb-2">{p.title}</h3>
              <p className="text-sm text-white/60">{p.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Verified Creators wall */}
      {verified.length > 0 && (
        <section className="relative z-10 max-w-6xl mx-auto px-5 py-12" data-testid="verified-wall">
          <div className="flex flex-col items-center text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/15 text-emerald-400 text-xs font-bold mb-3">
              <ShieldCheck className="w-3.5 h-3.5" /> VERIFIED ORIGINALS
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold mb-2">Real games. Real creators. Certified original.</h2>
            <p className="text-white/60 max-w-2xl">Every published game gets an AI-provenance scan and a Certificate of Authenticity — here are the latest that passed clean.</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {verified.map((g) => {
              const w = g.world || {};
              return (
                <button key={g.id} onClick={() => navigate(`/g/${g.id}`)} data-testid={`verified-card-${g.id}`}
                  className="group text-left rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden card-hover">
                  <div className="relative h-28 grid place-items-center overflow-hidden"
                    style={{ background: `linear-gradient(135deg, ${w.bgColor || "#0b1026"}, ${w.accent || "#7c3aed"})` }}>
                    {g.cover_image
                      ? <img src={`${process.env.REACT_APP_BACKEND_URL}${g.cover_image}`} alt={g.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform" />
                      : <span className="text-4xl group-hover:scale-110 transition-transform">{g.cover_emoji || "🎮"}</span>}
                    <span className="absolute bottom-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-black/55 text-emerald-300 backdrop-blur">
                      <ShieldCheck className="w-3 h-3" /> Verified
                    </span>
                  </div>
                  <div className="p-3">
                    <div className="font-bold text-sm truncate text-white">{g.title}</div>
                    <div className="text-xs text-white/50 truncate">by {g.owner_name}</div>
                  </div>
                </button>
              );
            })}
          </div>
          <div className="flex justify-center mt-8">
            <Button onClick={() => navigate("/register")} variant="outline" className="rounded-full border-white/20 text-white hover:bg-white/10" data-testid="verified-wall-cta">
              <Sparkles className="w-4 h-4" /> Publish your own verified game
            </Button>
          </div>
        </section>
      )}

      {/* Templates */}
      <section className="relative z-10 max-w-6xl mx-auto px-5 py-10">
        <div className="flex flex-wrap justify-center gap-3">
          {TEMPLATES.map((t) => (
            <span key={t} className="px-5 py-2.5 rounded-full bg-white/5 border border-white/10 text-sm font-semibold text-white/80">{t}</span>
          ))}
        </div>
      </section>

      {/* Roles */}
      <section className="relative z-10 max-w-6xl mx-auto px-5 py-16">
        <h2 className="font-display text-3xl sm:text-4xl font-bold text-center mb-3">Built for everyone</h2>
        <p className="text-center text-white/60 mb-12">From first-time creators to professional studios — there's a way in for you.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {ROLES.map((r, i) => (
            <Link key={r.title} to={`/for/${r.slug}`} data-testid={`role-card-${r.slug}`}
              className={`group glass rounded-2xl p-6 card-hover animate-rise delay-${(i % 4) + 1} block`}>
              <div className={`w-12 h-12 rounded-xl grid place-items-center bg-gradient-to-br ${r.c} mb-4`}>
                <r.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-display text-xl font-bold mb-2 flex items-center gap-1.5">{r.title}
                <ArrowRight className="w-4 h-4 text-white/40 group-hover:text-[#3b9eff] group-hover:translate-x-1 transition-all" />
              </h3>
              <p className="text-sm text-white/60">{r.text}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* Creator economy highlight */}
      <section className="relative z-10 max-w-6xl mx-auto px-5 py-16">
        <div className="glass rounded-3xl p-8 sm:p-12 overflow-hidden relative" data-testid="section-creator-economy">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#f59e0b]/15 blur-3xl" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#f59e0b]/15 text-[#f59e0b] text-xs font-bold mb-4"><Coins className="w-3.5 h-3.5" /> CREATOR ECONOMY</div>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold mb-3">Build it. Publish it. Grow it. <span className="text-brand-blue">Earn from it.</span></h2>
            <p className="text-white/60 max-w-2xl">Turn ideas into playable games, build up to 1000 levels, sell premium level packs, launch game passes, receive tips and grow your own creator profile — all kid-safe and creator-first.</p>
            <div className="grid sm:grid-cols-3 gap-3 mt-6">
              {["Up to 1000 AI-generated levels", "Premium level packs & game passes", "Creator analytics & growth coach"].map((t) => (
                <div key={t} className="flex items-start gap-2 text-sm text-white/80"><Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /> {t}</div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Feature pillars */}
      <section className="relative z-10 max-w-6xl mx-auto px-5 py-12">
        <h2 className="font-display text-3xl sm:text-4xl font-bold text-center mb-3">Everything in one platform</h2>
        <p className="text-center text-white/60 mb-12">Create, publish, play, compete and earn.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: Joystick, t: "GameView Arcade", d: "Play what the world creates — games by kids, creators, schools and studios.", c: "from-[#3b9eff] to-[#1f6feb]" },
            { icon: Coins, t: "Creator Economy", d: "Level packs, game passes, tips and a creator profile to grow your audience.", c: "from-[#f59e0b] to-[#ec4899]" },
            { icon: ShoppingBag, t: "Marketplace", d: "Buy & sell characters, worlds and assets that plug straight into AI generation.", c: "from-[#a855f7] to-[#7c3aed]" },
            { icon: Swords, t: "Arena & Jams", d: "Weekly challenges, game jams and tournaments with leaderboards.", c: "from-[#22c55e] to-[#14b8a6]" },
          ].map((p, i) => (
            <div key={p.t} className={`glass rounded-2xl p-6 card-hover animate-rise delay-${i + 1}`} data-testid={`pillar-${i}`}>
              <div className={`w-12 h-12 rounded-xl grid place-items-center bg-gradient-to-br ${p.c} mb-4`}><p.icon className="w-6 h-6 text-white" /></div>
              <h3 className="font-display text-lg font-bold mb-2">{p.t}</h3>
              <p className="text-sm text-white/60">{p.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Safety band */}
      <section className="relative z-10 max-w-6xl mx-auto px-5 py-12">
        <div className="grid md:grid-cols-2 gap-4">
          <div className="glass rounded-3xl p-8" data-testid="section-safety">
            <ShieldCheck className="w-9 h-9 text-emerald-400 mb-3" />
            <h3 className="font-display text-2xl font-bold mb-2">Safe creation for young imaginations</h3>
            <p className="text-white/60">Kids create games by voice while parents control publishing, privacy, playtime and purchases. Under-13 publishing and monetisation always require parent approval.</p>
          </div>
          <div className="glass rounded-3xl p-8" data-testid="section-school">
            <GraduationCap className="w-9 h-9 text-violet-400 mb-3" />
            <h3 className="font-display text-2xl font-bold mb-2">Game creation for the classroom</h3>
            <p className="text-white/60">Teachers turn lessons into playable games, assign projects, review student work and run private classroom Arcades — fully school-safe.</p>
          </div>
        </div>
      </section>

      {/* Pricing teaser */}
      <section className="relative z-10 max-w-6xl mx-auto px-5 py-12" data-testid="section-pricing">
        <h2 className="font-display text-3xl sm:text-4xl font-bold text-center mb-3">Start free. Grow when you're ready.</h2>
        <p className="text-center text-white/60 mb-10">Plans for creators, families and schools.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { n: "Free", p: "$0", d: "Create, play and publish with AI — perfect to get started." },
            { n: "Creator Pro", p: "$9", d: "More AI credits, no watermark, analytics and monetisation.", hot: true },
            { n: "Family", p: "Soon", d: "Multiple child accounts, parent controls, private family Arcade." },
            { n: "School", p: "Soon", d: "Teacher dashboard, student seats, classroom Arcade & lessons." },
          ].map((pl) => (
            <div key={pl.n} className={`rounded-2xl p-6 border ${pl.hot ? "border-[hsl(var(--primary))] glass brand-glow" : "border-white/10 glass"}`}>
              {pl.hot && <div className="text-[10px] font-bold text-[#3b9eff] mb-1">MOST POPULAR</div>}
              <div className="font-display text-xl font-bold">{pl.n}</div>
              <div className="font-display text-3xl font-extrabold my-2">{pl.p}<span className="text-sm font-normal text-white/50">{pl.p.startsWith("$") && pl.p !== "$0" ? "/mo" : ""}</span></div>
              <p className="text-sm text-white/60 mb-4">{pl.d}</p>
              <Button variant={pl.hot ? "default" : "outline"} className="rounded-full w-full" onClick={() => navigate("/pricing")}>
                {pl.p === "Soon" ? "Notify me" : "Get started"}
              </Button>
            </div>
          ))}
        </div>
        <div className="text-center mt-8">
          <Button variant="ghost" onClick={() => navigate("/pricing")} className="text-[#3b9eff] font-semibold" data-testid="see-all-plans">
            Compare all plans & features <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </section>

      {/* Imagine It. Build It. challenge (§7) */}
      <section className="relative z-10 max-w-5xl mx-auto px-5 py-8">
        <div className="relative overflow-hidden rounded-3xl border border-amber-400/30 bg-gradient-to-br from-amber-400/[0.12] via-fuchsia-500/[0.06] to-transparent p-8 sm:p-10 text-center" data-testid="landing-challenge-banner">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-400/20 text-amber-200 text-xs font-bold mb-3">
            <Trophy className="w-3.5 h-3.5" /> IMAGINE IT. BUILD IT.
          </div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold mb-2">Enter the community build challenge</h2>
          <p className="text-white/65 mb-6 max-w-xl mx-auto">Build a game from this season's theme, enter it in the gallery, and let players vote for their favourites.</p>
          <Button onClick={() => navigate("/challenge")} size="lg" className="rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-black font-bold" data-testid="landing-challenge-cta">
            <Sparkles className="w-4 h-4" /> See the challenge gallery
          </Button>
        </div>
      </section>

      <FoundingWaitlist />

      {/* CTA */}
      <section className="relative z-10 max-w-4xl mx-auto px-5 py-20 text-center">
        <div className="glass rounded-3xl p-10 sm:p-16 brand-glow">
          <h2 className="font-display text-3xl sm:text-5xl font-extrabold mb-4">
            Make game creation as <span className="text-brand-blue">easy as imagination.</span>
          </h2>
          <p className="text-white/60 mb-8 max-w-xl mx-auto">Join thousands of young creators, families and studios building the next generation of games.</p>
          <Button size="lg" onClick={() => navigate("/register")} data-testid="cta-register-btn"
            className="rounded-full text-base px-10 py-6 bg-gradient-to-r from-[#1f6feb] to-[#3b9eff] hover:opacity-90 brand-glow">
            Start building free <ArrowRight className="w-5 h-5" />
          </Button>
        </div>
      </section>

      <footer className="relative z-10 border-t border-white/5 py-8 px-5 text-center text-sm text-white/40">
        <div className="flex justify-center mb-3"><Logo size="sm" animated /></div>
        GameView Studio — AI game creation for the next generation.
      </footer>
    </div>
  );
}

function Particles() {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf, w, h, parts = [];
    const colors = ["rgba(168,85,247,", "rgba(59,158,255,", "rgba(245,158,11,", "rgba(226,232,240,"];
    const resize = () => {
      w = canvas.width = canvas.offsetWidth;
      h = canvas.height = canvas.offsetHeight;
      const n = Math.min(80, Math.floor(w / 20));
      parts = Array.from({ length: n }, () => ({
        x: Math.random() * w, y: Math.random() * h,
        r: Math.random() * 2.2 + 0.5,
        vy: -(Math.random() * 0.4 + 0.08), vx: (Math.random() - 0.5) * 0.25,
        a: Math.random() * 0.5 + 0.18, c: colors[Math.floor(Math.random() * colors.length)],
        tw: Math.random() * 6.28,
      }));
    };
    const tick = () => {
      ctx.clearRect(0, 0, w, h);
      for (const p of parts) {
        p.y += p.vy; p.x += p.vx; p.tw += 0.02;
        if (p.y < -12) { p.y = h + 12; p.x = Math.random() * w; }
        const alpha = p.a * (0.55 + 0.45 * Math.sin(p.tw));
        ctx.beginPath();
        ctx.fillStyle = p.c + alpha.toFixed(3) + ")";
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }
      raf = requestAnimationFrame(tick);
    };
    resize(); tick();
    window.addEventListener("resize", resize);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={ref} className="absolute inset-0 z-[2] w-full h-full pointer-events-none" data-testid="hero-particles" />;
}
